import type { z } from "zod";

import instance from "..";
import { looseParse } from "../looseParse";
import {
  type portfolioHistoryResponseSchema,
  portfolioHistoryUnionSchema,
} from "./portfolio.schema";

export type PortfolioHistoryResponse = z.infer<
  typeof portfolioHistoryResponseSchema
>;

type _PortfolioHistoryResponse = z.infer<typeof portfolioHistoryUnionSchema>;

export interface GetPortfolioArgs {
  accountID: string;
  product: string;
  intraday_reporting?: "market_hours" | "extended_hours" | "continuous";
  start?: string;
  end?: string;
  pnl_reset?: "per_day" | "no_reset";
  period?: "1D" | "1W" | "1M" | "1A" | "all";
  /** Bar size for the history series (e.g. ATD lookback `1Min` / `1D`). */
  timeframe?: string;
}

export const getPortfolioHistory = async ({
  accountID,
  product,
  start,
  end,
  pnl_reset,
  period = "1D",
  intraday_reporting = "extended_hours",
  timeframe,
}: GetPortfolioArgs): Promise<PortfolioHistoryResponse> => {
  const isLiveAccount = product === "live";

  const endpoint = `/${
    isLiveAccount ? "accounts" : "paper_accounts"
  }/${accountID}/portfolio/history`;

  const res = await instance.get<_PortfolioHistoryResponse>(endpoint, {
    params: {
      ...(start && { start }),
      ...(end && { end }),
      ...(pnl_reset && { pnl_reset }),
      ...(period && { period }),
      ...(intraday_reporting && { intraday_reporting }),
      ...(timeframe && { timeframe }),
    },
  });

  const parsed = looseParse(
    portfolioHistoryUnionSchema,
    res.data,
    "getPortfolioHistory",
  );

  // `looseParse` returns the raw payload unchanged when validation fails, so
  // `parsed` is not guaranteed to match either union arm here. Guard every
  // shape-dependent access (`"arrays" in parsed`, `.map(...)`) so a broken
  // envelope degrades to an empty history instead of throwing — honoring the
  // "never blocks" contract.
  const EMPTY_HISTORY: PortfolioHistoryResponse = {
    timestamp: [],
    equity: [],
    lmv: [],
    smv: [],
    profit_loss: [],
    profit_loss_pct: [],
    base_value: 0,
    timeframe: "",
  };

  if (!parsed || typeof parsed !== "object") {
    return EMPTY_HISTORY;
  }

  // Seconds → milliseconds (some payloads use epoch seconds).
  const toMillis = (timestamps: number[]): number[] =>
    timestamps.map((t) => (t < 1e12 ? t * 1000 : t));

  // transform DeprecatedPortfolioHistoryResponse to PortfolioHistoryResponse
  // Remove when DeprecatedPortfolioHistoryResponse is deleted
  if (
    "arrays" in parsed &&
    Array.isArray(parsed.arrays) &&
    Array.isArray(parsed.attributes)
  ) {
    const { arrays, attributes } = parsed;

    const getArrayByName = (name: string): number[] => {
      const idx = attributes.findIndex((attr) => attr?.name === name);
      const arr = idx !== -1 ? arrays[idx] : undefined;
      return Array.isArray(arr) ? arr : [];
    };

    return {
      timestamp: toMillis(getArrayByName("Date")),
      equity: getArrayByName("PortfolioValue"),
      lmv: getArrayByName("LMV"),
      smv: getArrayByName("SMV"),
      profit_loss: getArrayByName("ProfitLoss"),
      profit_loss_pct: getArrayByName("ProfitLossPctChange"),
      base_value: typeof parsed.base_value === "number" ? parsed.base_value : 0,
      timeframe: typeof parsed.timeframe === "string" ? parsed.timeframe : "",
    };
  }

  if ("timestamp" in parsed && Array.isArray(parsed.timestamp)) {
    return {
      ...parsed,
      timestamp: toMillis(parsed.timestamp),
      lmv: Array.isArray(parsed.lmv) ? parsed.lmv : [],
      smv: Array.isArray(parsed.smv) ? parsed.smv : [],
    };
  }

  return EMPTY_HISTORY;
};
